<template>
  <div class="glass-designer">
    <div class="gd-topbar" v-show="showToolbar">

      <CommandButton icon="fa-undo" label="Undo" @click="applyUndo" v-if="editing" />
      <CommandButton icon="fa-expand" label="Center" @click="resetView" />
      <CommandButton icon="fa-send-back" label="Arrange" @click="arrangePanels" v-if="editing" />
      <CommandDivider />

      <CommandButton icon="fa-arrows-alt-h" label="Dims" :disabled="!is2DView" :selected="options.displayDimensions"  @click="options.displayDimensions = !options.displayDimensions" />
      <CommandButton icon="fa-border-outer" label="Edgework" :disabled="!is2DView" :selected="options.displayEdgework" @click="options.displayEdgework = !options.displayEdgework" />
      <CommandButton icon="fa-exclamation-triangle" label="Warnings" :disabled="!is2DView" :selected="options.displayWarnings"  @click="options.displayWarnings = !options.displayWarnings" />
      <CommandButton icon="fa-border-inner" label="Snap" :disabled="!is2DView" :selected="options.snapEnabled" @click="options.snapEnabled = !options.snapEnabled" />
      <CommandButton icon="fa-border-all" label="Snap Grid" :disabled="!is2DView" :selected="options.snapGridEnabled" @click="options.snapGridEnabled = !options.snapGridEnabled" />
      <CommandButton icon="fa-cog" label="Settings" @click="displayEditorSettings" />
      <CommandDivider />

      <CommandButton :icon="viewLevelIcon" label="View" :selected="!isEditingMode" @click="displayViewLevelDropdown" />
      <CommandButton icon="fa-book" label="Documents" @click="displayOrderDocuments" />
      <CommandButton icon="fa-info-circle" label="Inspect" @click="displayInspectPopover" :badge="guidelineWarningList.length" :showSuccess="guidelineWarningList.length == 0" />
      <CommandDivider />

      <CommandButton icon="fa-mouse-pointer" label="Select" :disabled="!toolsEnabled || !isEditingMode" :active="activeToolType == ToolTypes.selectTool" @click="selectTool(ToolTypes.selectTool)" />
      <CommandButton icon="fa-scalpel-path" label="Cut" v-if="editing" :disabled="!toolsEnabled || !isEditingMode" :active="activeToolType == ToolTypes.cutTool" @click="selectTool(ToolTypes.cutTool)" />
      <CommandButton icon="fa-ruler" label="Measure" :disabled="!toolsEnabled || !isEditingMode" :active="activeToolType == ToolTypes.measureTool" @click="selectTool(ToolTypes.measureTool)" />
      <CommandButton icon="fa-square icon-skew-qtr" label="Skew" v-if="editing" :disabled="!toolsEnabled || !isEditingMode" :active="activeToolType == ToolTypes.skewTool" @click="selectTool(ToolTypes.skewTool)" />
      <CommandButton icon="fa-border-all" label="Split" v-if="editing" :disabled="!toolsEnabled || !isEditingMode" :active="activeToolType == ToolTypes.splitTool" @click="selectTool(ToolTypes.splitTool)" />
      <CommandDivider />

      <CommandButton icon="fa-exchange" label="Convert" v-if="editing" @click="displayConvertItem" />
      <CommandButton icon="fa-upload" label="Import" v-if="editing" @click="displayImport" />
      <CommandButton icon="fa-download" label="Export" @click="displayExport" />
      <CommandDivider v-if="showCloseButton || (orderEditorRequiresReview && editing)" />

      <CommandButton icon="fa-tasks" label="Review" v-if="editing && orderEditorRequiresReview" :badge="review.panelCount" :selected="review.visible" @click="displayReview" />
      <CommandButton icon="fa-shopping-cart" label="Order" v-if="showCloseButton && (!orderEditorRequiresReview || !editing)" @click="closeEditor" />

      <div class="gd-topbar-statsbar">
        <EditorInfoBar v-if="isDesktopMode" :mousePositionX="mousePosition.x" :mousePositionY="mousePosition.y" />
      </div>

    </div>
    <div class="gd-main-area">
      <div class="gd-leftbar" v-if="editing">
        <ToolbarDivider title="Templates" />
        <ToolbarButton icon="fa-draw-polygon" label="Shapes" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.shape)" @click="toggleLibrary('Shapes', LibraryItemTypes.shape)" />
        <ToolbarButton icon="fa-layer-group" label="Kits" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.kit)" @click="toggleLibrary('Kits', LibraryItemTypes.kit)" />
        <ToolbarButton icon="fa-list-alt" label="Assemblies" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.assembly)" @click="toggleLibrary('Assemblies', LibraryItemTypes.assembly)" />
        <ToolbarDivider />

        <ToolbarDivider title="Cutouts" />
        <ToolbarButton icon="fa-circle" label="Center" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.centerCutOut)" @click="toggleLibrary('Center Cutouts', LibraryItemTypes.centerCutOut)" />
        <ToolbarButton icon="fa-border-right" label="Edge" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.edgeCutOut)" @click="toggleLibrary('Edge Cutouts', LibraryItemTypes.edgeCutOut)" />
        <ToolbarButton icon="fa-border-style-alt" label="Corner" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.cornerCutOut)" @click="toggleLibrary('Corner Cutouts', LibraryItemTypes.cornerCutOut)" />        
        <ToolbarDivider />

        <ToolbarDivider title="Misc" />
        <ToolbarButton icon="fa-cube" label="Items" isLeftBar :disabled="!isEditingMode" :active="isLibraryTypeActive(LibraryItemTypes.panelItem)" @click="toggleLibrary('Panel Items', LibraryItemTypes.panelItem)" />
      </div>
      <div class="gd-library" v-show="library.visible">
        <EditorLibrarySideOverlay
          ref="librarySideBar"
          :title="library.title"
          :itemTypeFilter="library.type"
          :contactId="contactId"
          @libraryItemDraggedToEditor="libraryItemDraggedToEditor"
          @libraryItemClicked="libraryItemClicked"
          @libraryAddItemClicked="libraryAddItemClicked"
          @close="libraryClosed" />
      </div>
      <div class="gd-editor">
        <div class="gd-editor-top" v-show="options.displayRulers && is2DView && showRulers">
          <div class="gd-ruler-spacer" :style="rulerSpacerStyle" v-show="showRulers" />
          <Ruler ref="horizontalRuler" orientation="horizontal" :showMousePosition="isDesktopMode" :mousePosition="mousePosition.x" :originOffset="origin.x" :scale="zoomScale" :visible="options.displayRulers && is2DView" :theme="editorTheme" />
        </div>
        <div class="gd-editor-main">
          <Ruler ref="verticalRuler" orientation="vertical" :showMousePosition="isDesktopMode" :mousePosition="mousePosition.y" :originOffset="origin.y" :scale="zoomScale" v-show="options.displayRulers && is2DView && showRulers" :visible="options.displayRulers && is2DView && showRulers" :theme="editorTheme" />
          <Editor ref="editorComponent" 
            :isNewOrder="isNewOrder"
            :orderData="orderData"
            :orderType="orderType"
            :contactId="contactId"
            :newContactSalesRateId="newContactSalesRateId"
            :orderItemList="value"
            :editingEnabled="editing"
            :reviewEnabled="configReviewEnabled"
            @viewDataUpdated="editorViewDataUpdated"
            @viewportUpdated="editorViewportUpdated"
            @selectedToolUpdated="selectedToolUpdated"
            @toolsEnabled="setToolsEnabled"
            @userConfigUpdated="userConfigUpdated"
            @warningListUpdated="warningListUpdated"
            @panelReviewCountUpdated="panelReviewCountUpdated"
            @libraryUpdated="librarySidebarUpdated"
            @revertViewType="revertViewLevelType"
            @editorLoaded="editorLoaded"
            :displayDimensions="options.displayDimensions"
            :displayEdgework="options.displayEdgework"
            :displayWarnings="options.displayWarnings"
            :snapEnabled="options.snapEnabled"
            :snapGridEnabled="options.snapGridEnabled" />
        </div>
      </div>
      <div class="gd-review" v-show="review.visible">
        <EditorReviewSideBar
          ref="editorReviewBar"
          @closeReviewClicked="closeReview"
          @orderReviewCompleted="closeEditor"
          @viewDocumentsClicked="displayOrderDocuments"
          @itemHighlighted="reviewItemHighlighted"
        />
      </div>

      <!-- Dev log for device -->
      <div class="gd-console" ref="consoleLogDiv" v-if="consoleLog.length > 0">
        <span v-for="(message, index) in consoleLog" :key="`${index}-${message}`">
          {{message}}
        </span>
      </div>
    
      <EditorSelectionPopover
        ref="viewLevelPopover"
        title="View Type"
        :optionList="viewLevelList"
        v-model="options.viewLevel"
        :showSelection="true"
      />

      <EditorSelectionPopover
        popoverSizeClass="popover-large"
        ref="inspectionPopover"
        title="Guideline Warnings"
        emptyMessage="No production guideline warnings found"
        :optionList="guidelineWarningList"
        :showSelection="false"
        :scrollable="true"
        scrollHeight="200px"
        @clickedOption="guidelingWarningSelected"
      />
    </div>

    
  </div>
</template>

<style lang="scss">
@import "./../scss/editor.scss";

$right-bar-size: 62px;
$left-bar-size: 62px;
$library-size: 300px;
$review-size: 500px;
$top-bar-size: 58px;
$ruler-size: 18px;
$main-background-color: #323232;
$main-border: 1px solid #202020;//#9d9d9d;

.glass-designer {
  background-color: $main-background-color;
  width: 100%;
  height: 100%;
  max-height: 100%;
  border-top-left-radius: $layout-minor-border-radius;
  border-top-right-radius: $layout-minor-border-radius;

  display: flex;
  flex-direction: column;
  overflow: clip;
  align-items: stretch;
}
.gd-topbar {
  width: 100%;
  height: $top-bar-size;
  border-bottom: $main-border;

  display: flex;
  flex-direction: row;
  
  line-height: $top-bar-size;
  font-size: 20px;
}
.gd-topbar-leftbar-padding {
  padding-left: $left-bar-size;
}
.gd-topbar-library-padding {
  padding-left: calc(#{$left-bar-size} + #{$library-size}) !important;
}
.gd-topbar-button-gap {
  margin-left: auto !important;
}
.gd-topbar-statsbar {
  margin-left: auto !important;
  margin-right: 6px;
  width: 100px;
}
.gd-main-area {
  flex: 1;
  display: flex;
  flex-direction: row;
  max-height: calc(100vh - #{$top-bar-size});
}
.gd-library {
  width: $library-size;
  height: 100%;
  flex: 0 0 $library-size;
}
.gd-leftbar {
  width: $left-bar-size;
  flex: 0 0 $left-bar-size;
  height: 100%;
  border-right: $main-border;
  padding-top: 10px;
}
.gd-sidebar {
  width: $right-bar-size;
  flex: 0 0 $right-bar-size;
  height: 100%;
  border-left: $main-border;
  padding-top: 10px;
}
.gd-editor {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}
.gd-editor-top {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: $ruler-size;
}
.gd-editor-main {
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;
}
.gd-ruler-spacer {
  width: $ruler-size;
  height: $ruler-size;
  background-color: #ffffff;
}
.gd-review {
  width: $review-size;
  flex: 0 0 $review-size;
  height: 100%;
}

.gd-property {
  width: 300px;
  border-left: $main-border;
}

.gd-console {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  border: 1px solid #404040;
  background-color: #ffffff;
  color: #ff0000;
  height: 100px;
  overflow-x: hidden;
  overflow-y: scroll;
  font-size: 10px;
  white-space: pre;
  line-height: 8px;
}
</style>

<script>
import _ from 'lodash'
import { mapState } from "vuex"
import { saveAs } from 'file-saver'

import OrderItemMixin from '@/modules/orders/mixins/orderItemMixin'

import ToolbarButton from './../components/toolbars/toolbarButton.vue'
import ToolbarDivider from './../components/toolbars/toolbarDivider.vue'
import CommandButton from './../components/toolbars/commandButton.vue'
import CommandDivider from './../components/toolbars/commandDivider.vue'
import EditorInfoBar from './../components/toolbars/editorInfoBar.vue'
import EditorLibrarySideOverlay from './../components/library/editorLibrarySideOverlay'
import EditorReviewSideBar from './../components/editorReviewSideBar'
import EditorSelectionPopover from './../components/editorSelectionPopover'

import DocumentPreviewDialog from '@/components/dialog/documentPreviewDialog'
import LibraryItemDialog from './libraryItemDialog'
import EditorSettingsDialog from './editorSettingsDialog'
import ImportDXFDialog from './importDXFDialog.vue'
import ConvertOrderItemDialog from './convertOrderItemDialog.vue'

import Ruler from './../components/ruler.vue'
import Editor from './../components/editor.vue'

import { LinkTypes, AccountSettingKeys } from '@/store/types'
import { EditorModeTypes, LibraryItemTypes, ToolTypes, EditorViewLevelTypes } from './../types'

export default {
  name: 'GlassDesignerApp',
  model: {
    prop: 'value',
    event: 'update'
  },
  mixins: [OrderItemMixin],
  components: {
    CommandButton,
    CommandDivider,
    ToolbarButton,
    ToolbarDivider,
    Ruler,
    Editor,
    EditorLibrarySideOverlay,
    EditorReviewSideBar,
    EditorSelectionPopover,
    EditorInfoBar
  },
  props: {
    orderData: Object,
    orderType: String,
    sequenceNumber: String,
    isNewOrder: Boolean,
    orderId: Number,
    contactId: [Number, String],
    newContactSalesRateId: Number,     // New contacts have a selectable sales rate
    customerData: Object,
    orderDocumentUploadList: Array,   // Uploads
    editing: {
      type: Boolean,
      default: true
    },
    availableViewLevelTypes: Array,
    showToolbar: {
      type: Boolean,
      default: true
    },
    showRulers: {
      type: Boolean,
      default: true
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },

    value: Array
  },
  data: () => {
    return {
      options: {
        displayDimensions: true,
        displayWarnings: true,
        displayRulers: true,
        displayEdgework: true,
        snapEnabled: true,
        snapGridEnabled: false,
        viewLevel: EditorViewLevelTypes.normal
      },
      zoomScale: 1.0,
      origin: {
        x: 0,
        y: 0
      },
      mousePosition: {
        x: 0,
        y: 0
      },
      activeToolType: '',
      toolsEnabled: true,
      guidelineWarningList: [],
      library: {
        title: '',
        type: '',
        visible: false,
      },
      review: {
        panelCount: 0,
        visible: false
      },
      viewLevelList: [
        { name: 'Normal', value: EditorViewLevelTypes.normal, description: 'Standard editing view mode', icon: 'fas fa-layer-group' },
        { name: 'Cutting', value: EditorViewLevelTypes.cutting, description: 'Shows the straight line cutting details', icon: 'fas fa-scalpel-path' },
        { name: 'Cutouts', value: EditorViewLevelTypes.cutouts, description: 'Shows the cutout details', icon: 'fas fa-draw-square' },
        { name: 'Technical', value: EditorViewLevelTypes.technical, description: 'Shows the full technical details of the shape', icon: 'fas fa-drafting-compass' },
        //{ name: '3D', value: EditorViewLevelTypes.view3D },   // Disabled until cleaned up
      ],
      editorTheme: {},
      order: {},
      orderItems: []
    }
  },
  watch: {
    consoleLog: function() {
      this.$nextTick(() => {
        this.$refs.consoleLogDiv.scrollTop = this.$refs.consoleLogDiv.scrollHeight
      })
    },
    'library.visible': function() {
      this.$nextTick(() => {
        this.$refs.editorComponent.onResize()
      })
    },
    'review.visible': function() {
      this.$nextTick(() => {
        this.$refs.editorComponent.onResize()
      })
    },
    'options.viewLevel': function() {
      let info = this.viewLevelList.find(levelInfo => levelInfo.value == this.options.viewLevel)
      if(info) {
        // Show/Hide the overlay
        if(this.options.viewLevel == EditorViewLevelTypes.normal)
          this.$refs.editorComponent.hideViewTypeOverlay()
        else if(!this.availableViewLevelTypes)
          this.$refs.editorComponent.showViewTypeOverlay(info.icon, `View Type - ${info.name}`)
      }

      this.$refs.editorComponent.editor.setViewLevelType(this.options.viewLevel)
      this.options.viewLevel = this.$refs.editorComponent.editor.getViewLevel()

      if(this.options.viewLevel != EditorViewLevelTypes.normal)
        this.libraryClosed()
    }
  },
  computed: {
    isDesktopMode() {
      if(this.$refs.editorComponent && this.$refs.editorComponent.editor)
        return !this.$refs.editorComponent.editor.settings.isTouchDevice
      return true
    },
    ...mapState({
      consoleLog: state => state.system.consoleLog,
      accountSettings: state => state.account.accountSettings
    }),
    isEditingMode() {
      return this.options.viewLevel == EditorViewLevelTypes.normal
    },
    is2DView() {
      return this.options.viewLevel != EditorViewLevelTypes.view3D
    },
    rulerSpacerStyle() {
      return {
        'background-color': this.editorTheme.rulerBackground || '#ffffff'
      }
    },
    viewLevelIcon() {
      let info = this.viewLevelList.find(levelInfo => levelInfo.value == this.options.viewLevel)
      if(info)
        return info.icon
      return 'fa-layer-group'
    },
    configReviewEnabled() {
      if(!this.accountSettings)
        return false

      return parseInt(this.accountSettings[AccountSettingKeys.orderEditorRequiresReview] || 0) != 0
    },
    orderEditorRequiresReview() {
      if(!this.accountSettings)
        return false

      // If no items exist, the order doesn't require review so display the close button to be able to exit
      if(this.review.panelCount == 0)
        return false

      return parseInt(this.accountSettings[AccountSettingKeys.orderEditorRequiresReview] || 0) != 0
    }
  },
  created() {
    this.EditorViewLevelTypes = EditorViewLevelTypes
    this.ToolTypes = ToolTypes
    this.LibraryItemTypes = LibraryItemTypes
  },
  mounted() {
    // Disable scrolling while editor active
    document.body.classList.add('app-noscroll', 'app-editormode')
    window.scrollTo(0, 0)
  },
  beforeDestroy() {
    document.body.classList.remove('app-noscroll', 'app-editormode')
  },
  methods: {
    editorViewportUpdated(evt) {
      this.$data.zoomScale = evt.zoomScale
      this.$data.origin = evt.origin
      if(evt.mousePosition)
        this.$data.mousePosition = evt.mousePosition

      if(this.$refs.horizontalRuler)
        this.$refs.horizontalRuler.setRequiresRender()
      if(this.$refs.verticalRuler)
        this.$refs.verticalRuler.setRequiresRender()
    },
    editorViewDataUpdated(evt) {
      this.$data.zoomScale = evt.zoomScale
      this.$data.origin = evt.origin
      if(evt.mousePosition)
        this.$data.mousePosition = evt.mousePosition

      if(this.$refs.horizontalRuler)
        this.$refs.horizontalRuler.setRequiresRender()
      if(this.$refs.verticalRuler)
        this.$refs.verticalRuler.setRequiresRender()
    },
    closeEditor() {
      // Build the order item list
      this.displayProcessingDialog({
        title: 'Glass Editor',
        message: this.editing ? 'Building order item list' : 'Closing',
        icon: 'fa-clipboard-list-check',
        onBeforeOpen: (instance) => {
          this.$refs.editorComponent.editor.updateOrderItemList()
          .then(() => {
            instance.hide()

            let orderObj = this.$refs.editorComponent.editor.getOrder()
            
            this.orderItems = this.buildOrderItemList(orderObj.orderItems || [])
            this.$emit('update', this.orderItems)

            document.body.classList.remove('app-noscroll', 'app-editormode')
            this.$emit('close')
          })
          .catch(err => {
            instance.hide()
            console.error(err)
          })
        }
      }, {
        mountBody: true
      })
    },
    applyUndo() {
      this.$refs.editorComponent.editor.undoLastAction()
    },
    resetView() {
      this.$refs.editorComponent.editor.resetView()
    },
    arrangePanels() {
      this.$refs.editorComponent.editor.arrangePanels()
      this.$refs.editorComponent.editor.resetView()
    },
    revertViewLevelType() {
      this.options.viewLevel = EditorViewLevelTypes.normal
    },
    userConfigUpdated(data) {
      let userConfigData = data.userConfigData
      
      this.options.displayRulers = userConfigData.appRulersEnabled
      this.editorTheme = _.cloneDeep(data.themeData)
    },
    warningListUpdated(list) {
      this.guidelineWarningList = list.map(guidelineWarning => {
        return {
          id: guidelineWarning.id,
          value: guidelineWarning.id,
          icon: guidelineWarning.getIcon(),
          name: guidelineWarning.getName(),
          description: guidelineWarning.toHTMLString()
        }
      })
    },
    guidelingWarningSelected(warningId) {
      this.$refs.editorComponent.editor.guidelineWarningClicked(warningId)
    },
    panelReviewCountUpdated(count) {
      this.review.panelCount = count
    },
    selectedToolUpdated(toolType) {
      this.activeToolType = toolType
    },
    setToolsEnabled(enabled) {
      this.toolsEnabled = enabled
    },

    selectTool(toolType) {
      this.$refs.editorComponent.editor.startTool(toolType)
    },

    toggleLibrary(title, libraryItemType) {
      if(this.library.visible && this.library.type == libraryItemType) {
        this.library.visible = false
        this.library.type = null
      } else {
        this.library.title = title
        this.library.visible = true
        this.library.type = libraryItemType
      }
    },
    libraryItemDraggedToEditor(item) {
      this.$refs.editorComponent.editor.libraryItemDraggedToEditor(item)
    },
    libraryItemClicked(item) {
      // Show editor (only for library items not a core built-in item)
      if(item.id && !item.isCoreItem) {
        this.displayModal(LibraryItemDialog, {
          isCreateMode: false,
          id: item.id
        }, {}, { mountBody: true })
        .then(libraryItemInfo => {
          if(libraryItemInfo) {
            if(libraryItemInfo.id)
              this.successToast('Item Updated')
            else if(libraryItemInfo.deleted)
              this.successToast('Item Deleted')

            this.librarySidebarUpdated()
          }
        })
      }
    },
    libraryAddItemClicked() {
      // Note: Only panel items can be created directly from library sidebar
      this.displayModal(LibraryItemDialog, {
        isCreateMode: true,
        typeFilter: [LibraryItemTypes.panelItem]
      }, {}, { mountBody: true })
      .then(libraryItemInfo => {
        if(libraryItemInfo) {
          this.successToast('Item Added')
          this.librarySidebarUpdated()
        }
      })
    },
    libraryClosed() {
      this.library.visible = false
      this.library.type = null
    },
    librarySidebarUpdated() {
      this.$refs.librarySideBar.reloadData()

      // Reload editor config data
      this.$refs.editorComponent.editor.loadConfigurationData()
      .then(() => {
      })
    },
    isLibraryTypeActive(libraryItemType) {
      return this.library.visible && this.library.type == libraryItemType
    },
    displayViewLevelDropdown(evt) {
      this.$refs.viewLevelPopover.displayFromElement(evt.target)
    },
    displayInspectPopover(evt) {
      this.$refs.inspectionPopover.displayFromElement(evt.target)
    },
    displayEditorSettings() {
      this.displayModal(EditorSettingsDialog, {
        editor: this.$refs.editorComponent.editor
      },
      this.$refs.editorComponent.editor.getUserConfig(),
      { mountBody: true })
      .then(config => {
        if(config)
          this.$refs.editorComponent.editor.updateUserConfig(config)
      })
    },
    displayConvertItem() {
      this.displayModal(ConvertOrderItemDialog, {
        order: this.$refs.editorComponent.editor.getOrder()
      }, {}, { mountBody: true })
      .then(orderItemList => {

        if(orderItemList) {
          this.$refs.editorComponent.editor.convertLineOrderItems(orderItemList)
        }
      })
    },
    displayImport() {
      this.displayModal(ImportDXFDialog, {
        contactId: this.$refs.editorComponent.editor.getOrder().contactId,
        defaultSalesRateId: this.$refs.editorComponent.editor.getOrder().newCustomerDefaultSalesRateId,
        editorConfigData: this.$refs.editorComponent.editor.getEditorConfigurationData()
      }, {}, { mountBody: true })
      .then(importData => {

        if(importData) {
          this.$refs.editorComponent.editor.importGlassShapeList(importData)
        }

      })
    },
    displayExport() {
      // Just download raw DXF for now
      // In future may show options / shape selection
      let dxfData = this.$refs.editorComponent.editor.generateExportDXF()

      // Download
      var dxfBlob = new Blob([dxfData], {
        type: 'application/dxf'
      });

      let orderInfo = ''
      if(this.sequenceNumber)
        orderInfo = `-${this.sequenceNumber}`
      
      var filename = `order${orderInfo}.dxf`
      saveAs(dxfBlob, filename)
    },
    displayOrderDocuments() {
      let docProps = {
        uploadList: this.orderDocumentUploadList || [],
      }
      if(this.orderId) {
        docProps.linkId = this.orderId
        docProps.linkType = LinkTypes.order
      }

      this.displayModal(DocumentPreviewDialog, docProps, {}, { mountBody: true })
      .then(() => {
        
      })
    },

    displayReview() {
      if(!this.review.visible) {
        this.review.visible = true
        this.$refs.editorReviewBar.startReview(this.$refs.editorComponent.editor)

        this.options.viewLevel = EditorViewLevelTypes.technical
      } else {
        this.review.visible = false
        this.options.viewLevel = EditorViewLevelTypes.normal
      }
    },
    closeReview() {
      this.review.visible = false
      this.options.viewLevel = EditorViewLevelTypes.normal
    },
    reviewItemHighlighted(bounds) {
      if(bounds)
        this.$refs.editorComponent.editor.displayReviewHighlightRegion(bounds)
      else
        this.$refs.editorComponent.editor.hideReviewHighlightRegion()
    },

    editorLoaded() {
      // If using restricted view types, force the first one on the editor
      if(this.availableViewLevelTypes && this.availableViewLevelTypes.length > 0) {
        this.options.viewLevel = this.availableViewLevelTypes[0]

        this.$refs.editorComponent.editor.setViewLevelType(this.options.viewLevel)
      }
    }
  }
}
</script>
